import { Injectable } from "@angular/core";
import { ErrorMessage } from "../../../classes/general/error-message";
import { AppSettings } from "../../utils/app-settings";
import { ETrackedEvents } from 'src/app/classes/app/analytics';
import { AppConstants } from 'src/app/classes/app/constants';
import { AnalyticsFirebaseService } from './analytics-firebase';
import { AnalyticsTemplateService } from './analytics-template';
import { GeneralCache } from 'src/app/classes/app/general-cache';
import { AnalyticsLeplaceService } from "./analytics-leplace";
import { PromiseUtils } from "../../utils/promise-utils";
import { MQTTService } from "../../telemetry/mqtt.service";
import { Subscription } from "rxjs";
import { AppVersionService } from "../app-version";


enum EAnalyticsProvider {
    firebase = 1
}

// event tracking on firebase analytics
@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    enabled: boolean = false;
    enabledCustom: boolean = true;

    initialized: boolean = false;
    mode: number = EAnalyticsProvider.firebase;
    analyticsService: AnalyticsTemplateService = null;

    watchTelemetryCmd: Subscription = null;

    origConsole = {
        log: null,
        error: null,
        warn: null
    };

    telemetryEnabled: boolean = false;

    constructor(
        public firebase: AnalyticsFirebaseService,
        public leplace: AnalyticsLeplaceService,
        public appVersion: AppVersionService,
        public mqtt: MQTTService
    ) {
        console.log("analytics service created");
        console.log("analytics: ", this.enabled);
    }

    init() {
        // service factory
        switch (this.mode) {
            case EAnalyticsProvider.firebase:
                this.analyticsService = this.firebase;
                break;
            default:
                break;
        }

        this.initialized = true;
        return this.analyticsService.init();
    }

    startWatchTelemetryCommands() {
        this.mqtt.subscribeToCommands();
        this.watchTelemetryCmd = this.mqtt.getWatchTelemetryCommands().subscribe((enable: boolean) => {
            if (enable != null) {
                this.toggleStreamToMQTT(enable);
            }
        }, (err: Error) => {
            console.error(err);
        });
    }

    checkTelemetryWizard() {
        console.log(
            "check telemetry logs customEventLogger: " + AppConstants.gameConfig.customEventLogger +
            ", remoteConsoleLogger: " + AppConstants.gameConfigUser.useRemoteConsoleLogger +
            ", requestLogger: " + AppConstants.gameConfigUser.useRequestLogger +
            ", sendConsoleLogsAlways: " + AppConstants.gameConfig.sendConsoleLogsAlways);

        if (AppConstants.gameConfig.customEventLogger) {
            this.configureTelemetry(true, !(AppConstants.gameConfigUser.useRemoteConsoleLogger || AppConstants.gameConfig.sendConsoleLogsAlways), false);
        }
    }

    toggleStreamToMQTT(enable: boolean) {
        console.log("toggle stream to MQTT: ", enable);
        this.leplace.toggleStreamToMQTT(enable);
        if (enable) {
            this.configureTelemetry(true, null, true);
        } else {
            this.configureTelemetry(false, false, false);
        }
    }

    /**
     * this must be called on platform init
     */
    initializeConsoleLogger() {
        this.origConsole.log = console.log;
        this.origConsole.error = console.error;
        this.origConsole.warn = console.warn;
    }

    /**
     * send store logs to server for inspection
     */
    configureTelemetry(enable: boolean, iapOnly: boolean, streaming: boolean) {
        var self = this;

        console.log("configure telemetry > enable: " + enable + " iap only: " + iapOnly + " streaming: " + streaming);

        let getConsoleLogString = (args: IArguments) => {
            let argstring: string = "";
            for (let i = 0; i < args.length; i++) {
                argstring += args[i];
                if (i < args.length - 1) {
                    argstring += ", ";
                }
            }
            return argstring;
        };

        GeneralCache.appFlags.telemetry = enable;

        if (!enable) {
            if (!GeneralCache.isPublicDistribution) {
                window.console.log = this.origConsole.log;
                window.console.error = this.origConsole.error;
                window.console.warn = this.origConsole.warn;
            }
            this.telemetryEnabled = false;
            return;
        } else {
            if (this.telemetryEnabled) {
                console.warn("telemetry already enabled");
                return;
            }
        }

        window.console.log = function () {
            try {
                if (enable) {
                    if (arguments.length > 0) {
                        try {
                            if (arguments[0].indexOf("[store.js") !== -1) {
                                self.leplace.sendEvent("leplace_IAP_log", getConsoleLogString(arguments), "log", 0);
                            } else {
                                if (!iapOnly && !streaming) {
                                    self.leplace.sendEvent("leplace_console_log", getConsoleLogString(arguments), "log", 0);
                                }
                            }
                            if (streaming) {
                                self.leplace.sendEvent("leplace_console_log", getConsoleLogString(arguments), "log", 0);
                            }
                        } catch (err) {

                        }
                    }
                }
                if (!GeneralCache.isPublicDistribution) {
                    self.origConsole.log.apply(console, arguments); // preserve original function (console logger)
                }
            } catch (err) {
                console.error(err);
            }
        };
        window.console.error = function () {
            try {
                if (enable) {
                    if (arguments.length > 0) {
                        try {
                            if (!iapOnly && !streaming) {
                                self.leplace.sendEvent("leplace_console_error", getConsoleLogString(arguments), "error", 0);
                            }
                            if (streaming) {
                                self.leplace.sendEvent("leplace_console_error", getConsoleLogString(arguments), "error", 0);
                            }
                        } catch (err) {

                        }
                    }
                }
                if (!GeneralCache.isPublicDistribution) {
                    self.origConsole.error.apply(console, arguments); // preserve original function (console logger)
                }
            } catch (err) {
                console.error(err);
            }
        };
        window.console.warn = function () {
            try {
                if (enable) {
                    if (arguments.length > 0) {
                        try {
                            if (!iapOnly && !streaming) {
                                self.leplace.sendEvent("leplace_console_warn", getConsoleLogString(arguments), "warn", 0);
                            }
                            if (streaming) {
                                self.leplace.sendEvent("leplace_console_warn", getConsoleLogString(arguments), "warn", 0);
                            }
                        } catch (err) {

                        }
                    }
                }
                if (!GeneralCache.isPublicDistribution) {
                    self.origConsole.warn.apply(console, arguments); // preserve original function (console logger)
                }
            } catch (err) {
                console.error(err);
            }
        };
    }

    /**
     * set user id from the db
     * @param userId 
     */
    setUserId(userId: number) {
        if (!this.enabled) {
            return;
        }
        this.analyticsService.setUserId(userId);
    }

    /**
     * track view (changes)
     * @param title 
     */
    trackView(title: string) {
        if (!this.enabled) {
            return;
        }
        console.log("track view: " + title);
        this.analyticsService.trackView(title);
    }


    /**
     * this method can save error messages into an array 
     * to show them in debug mode in the interface
     */
    dispatchError(err: any, type: string = "") {
        if (!this.enabled) {
            return;
        }

        // errors are sent regardless of the user preference
        this.sendEvent(ETrackedEvents.error, ErrorMessage.parse(err), type, 0, true);
    }


    setEnabled(val: boolean) {
        console.log("set analytics: ", val);
        this.enabled = val;
    }

    /**
    * send event to analytics service
    * custom event category (may be disabled via app flag)
    * @param category 
    * @param action 
    * @param label 
    * @param value 
    */
    sendCustomEvent(category: string, action: string, label: string, value: number, customOnly: boolean): Promise<boolean> {
        if (!this.enabledCustom) {
            return;
        }
        if (!customOnly) {
            return this.sendEvent(category, action, label, value, false);
        }
        if (AppConstants.gameConfig.customEventTracking === 1) {
            return this.sendEvent(category, action, label, value, false);
        } else {
            console.warn("custom event tracking is disabled");
            return Promise.resolve(false);
        }
    }

    /**
     * send event to analytics service
     * @param category 
     * @param action 
     * @param label 
     * @param value 
     * @param force 
     */
    sendEvent(category: string, action: string, label: string, value: number, force: boolean = false): Promise<boolean> {
        if (!(this.enabled || this.enabledCustom)) {
            return;
        }
        let promise: Promise<boolean> = null;
        if (this.initialized && AppSettings.globalEnableAnalytics) {
            // default logger
            console.log("send event");
            if (this.enabled || force) {
                promise = this.analyticsService.sendEvent(category, action, label, value);
            } else {
                promise = Promise.resolve(false);
            }
        } else {
            promise = Promise.resolve(false);
        }

        // additional event processing
        if (AppConstants.gameConfig.customEventLogger) {
            // custom logger
            console.log("send custom event");
            PromiseUtils.wrapNoAction(this.leplace.sendEvent(category, action, label, value), true);
        }

        return promise;
    }
}
